import { useEffect, useRef, type ReactElement } from 'react';
import EvidenceTable from './EvidenceTable';
import { useMegaGridState, type MegaGridColumn } from '@/design/MegaGrid/MegaGrid';
import type { InteractionRecord } from '@/shared/analytics/AnalyticsTypes';
import type { VizAccent } from '@/design/VizAccents';

interface SeededEvidenceTableProps {
    records: InteractionRecord[];
    accent: VizAccent;
    columns: MegaGridColumn<InteractionRecord>[];
    persistenceKey: string;
    clientPageSize?: number;
    /** Column key to sort by descending on first load, when the grid has no persisted/active sort. */
    defaultSortKey: string;
}

/**
 * An {@link EvidenceTable} that opens sorted by a chosen column (descending) —
 * the token drill-in wants heaviest-total-tokens first. Mirrors the session-drift
 * grid: it owns an external controller and seeds the sort once, only when the
 * grid has no sort yet so a user's saved/explicit sort still wins.
 */
export default function SeededEvidenceTable({ records, accent, columns, persistenceKey, clientPageSize, defaultSortKey }: SeededEvidenceTableProps): ReactElement {
    const controller = useMegaGridState({
        columns,
        persistenceKey,
        defaultDensity: 'comfortable',
        defaultGroupBy: [],
        allowMultiSort: true,
    });

    const seeded = useRef(false);
    useEffect(() => {
        if (seeded.current) return;
        seeded.current = true;
        if (controller.state.sorts.length === 0) {
            controller.applyStatePatch({ sorts: [{ key: defaultSortKey, dir: 'desc' }] });
        }
    }, [controller, defaultSortKey]);

    return <EvidenceTable records={records} accent={accent} columns={columns} controller={controller} clientPageSize={clientPageSize} />;
}
